import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Icon from "@mdi/react";
import {
    mdiCalendarMultiple,
    mdiPlus,
    mdiPencil,
    mdiTrashCan,
    mdiLock,
    mdiLockOpen,
    mdiSchool,
    mdiArrowRight,
    mdiChevronDown,
    mdiChevronUp,
    mdiRefresh,
    mdiHelpCircleOutline,
    mdiHelpCircle,
    mdiAccountGroup,
    mdiLayersOutline,
    mdiAlertOutline,
    mdiEmailOutline,
    mdiCreditCardFastOutline,
} from "@mdi/js";
import BaseModal from "../../components/BaseModal";
import FolderTabs from "../../components/FolderTabs";
import ClassTermsHelpModal from "../../components/ClassTermsHelpModal";
import DateInputWithPicker from "../../components/DateInputWithPicker";
import { toast } from "react-toastify";
import AppHeader from "../../components/AppHeader";
import { classApi } from "../../api/classes";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomFormInput from "../../components/forms/CustomFormInput";
import EmailReviewModal from "../../components/communications/EmailReviewModal";
import { getDanceStyles, getDanceStyleColor, getDanceStyleLabel, resolveToId } from "../../config/danceStyles";
import { formatCurrencyFromMinor } from "../../utils/currency";
import "../Common.css";
import "../Dashboard.css";
import "../header.css";
import "./ClassTermsPage.css";
// DAY_OPTIONS indexed 0-6 matching dayOfWeek schema: 0=Sun,1=Mon,...,6=Sat
const DAY_OPTIONS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const TERM_GRID_PX_PER_HOUR = 60;
const termTimeToMinutes = t => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + (m || 0);
};

/**
 * ClassTermsPage - Sub-Portal Feature for term/season management
 * Portal type: Sub-Portal Feature (.sub-portal-header-card)
 */
const ClassTermsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [terms, setTerms] = useState([]);
    const [classesMap, setClassesMap] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    // Term form state
    const [showForm, setShowForm] = useState(false);
    const [editingTerm, setEditingTerm] = useState(null);
    const [formName, setFormName] = useState("");
    const [formType, setFormType] = useState("term");
    const [formStartDate, setFormStartDate] = useState("");
    const [formEndDate, setFormEndDate] = useState("");
    const [formEnrolmentOpen, setFormEnrolmentOpen] = useState(false);
    const [formDurationWeeks, setFormDurationWeeks] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    // Wizard state (new term creation - 3-step)
    const [wizardStep, setWizardStep] = useState(0); // 0=closed, 1, 2, 3
    const [isWizardSubmitting, setIsWizardSubmitting] = useState(false);
    const [wizardRolloverSelected, setWizardRolloverSelected] = useState(true);
    // Default ON — when an admin rolls over students, the most common intent is
    // to also email them so they can confirm their spot before the term starts.
    // Without the email, students appear as "unconfirmed" and would need to be
    // chased manually or confirmed in person. Admin can untick if they want a
    // silent rollover (e.g. they'll handle confirmations another way).
    const [wizardEmailRollover, setWizardEmailRollover] = useState(true);

    // Short course state
    const [shortCourses, setShortCourses] = useState([]);
    const [showShortCourseModal, setShowShortCourseModal] = useState(false);
    const [editingShortCourse, setEditingShortCourse] = useState(null);
    const [scName, setScName] = useState("");
    const [scStyle, setScStyle] = useState("");
    const [scLevel, setScLevel] = useState("");
    const [scStartDate, setScStartDate] = useState("");
    const [scSessionCount, setScSessionCount] = useState(4);
    const [scStartTime, setScStartTime] = useState("19:00");
    const [scEndTime, setScEndTime] = useState("20:00");
    const [scRequiresApproval, setScRequiresApproval] = useState(true);
    const [scShortCourseGroupId, setScShortCourseGroupId] = useState("");
    const [scGuestInstructorName, setScGuestInstructorName] = useState("");
    const [isScSaving, setIsScSaving] = useState(false);

    // Short course group state
    const [shortCourseGroups, setShortCourseGroups] = useState([]);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [grpName, setGrpName] = useState("");
    const [grpLabel, setGrpLabel] = useState("");
    const [grpDescription, setGrpDescription] = useState("");
    const [grpStartDate, setGrpStartDate] = useState("");
    const [grpEndDate, setGrpEndDate] = useState("");
    const [grpLocation, setGrpLocation] = useState("");
    const [isGrpSaving, setIsGrpSaving] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, variant, onConfirm }

    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);

    const [collapsedTerms, setCollapsedTerms] = useState(new Set());
    const toggleTermCollapse = termId => {
        setCollapsedTerms(prev => {
            const next = new Set(prev);
            next.has(termId) ? next.delete(termId) : next.add(termId);
            return next;
        });
    };

    const addOneHour = (timeStr) => {
        const [h, m] = timeStr.split(":").map(Number);
        const total = h * 60 + m + 60;
        const hh = String(Math.floor(total / 60) % 24).padStart(2, "0");
        const mm = String(total % 60).padStart(2, "0");
        return `${hh}:${mm}`;
    };

    // Class modal state
    const [showClassModal, setShowClassModal] = useState(false);
    const [classModalTermId, setClassModalTermId] = useState(null);
    const [editingClass, setEditingClass] = useState(null);
    const [classFormName, setClassFormName] = useState("");
    const [classFormStyle, setClassFormStyle] = useState("");
    const [classFormDay, setClassFormDay] = useState(1);
    const [classFormStartTime, setClassFormStartTime] = useState("19:00");
    const [classFormEndTime, setClassFormEndTime] = useState("20:00");
    const [classFormLocation, setClassFormLocation] = useState("");
    const [classFormMaxStudents, setClassFormMaxStudents] = useState("");
    const [classFormDescription, setClassFormDescription] = useState("");
    const [isClassSaving, setIsClassSaving] = useState(false);

    // Class import state (reused by wizard Step 2)
    const [importSourceTermId, setImportSourceTermId] = useState("");
    const [importCandidateClasses, setImportCandidateClasses] = useState([]);
    const [selectedClassIds, setSelectedClassIds] = useState(new Set());
    const [isLoadingImportClasses, setIsLoadingImportClasses] = useState(false);

    // Open-enrolment confirmation gate state
    const [showOpenEnrolmentConfirmModal, setShowOpenEnrolmentConfirmModal] = useState(false);
    const [openEnrolmentConfirmTerm, setOpenEnrolmentConfirmTerm] = useState(null);
    const [showCloseEnrolmentConfirmModal, setShowCloseEnrolmentConfirmModal] = useState(false);
    const [closeEnrolmentConfirmTerm, setCloseEnrolmentConfirmTerm] = useState(null);

    // Post-open-enrolment email prompt state
    const [showEnrolmentReminderModal, setShowEnrolmentReminderModal] = useState(false);
    const [enrolmentReminderTermId, setEnrolmentReminderTermId] = useState(null);
    const [enrolmentReminderContext, setEnrolmentReminderContext] = useState('standalone'); // 'open' | 'standalone'
    const [isSendingReminders] = useState(false);

    // Email review modal state
    const [showEmailReviewModal, setShowEmailReviewModal] = useState(false);
    const [emailReviewType, setEmailReviewType] = useState('');
    const [emailReviewData, setEmailReviewData] = useState({});
    const [emailReviewOnConfirm, setEmailReviewOnConfirm] = useState(null);
    const [emailReviewAudienceDescription, setEmailReviewAudienceDescription] = useState('');
    const [emailReviewRecipientCount, setEmailReviewRecipientCount] = useState(null);
    const [isConfirmingSend, setIsConfirmingSend] = useState(false);

    // Class cancellation modal state (B20)
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelPreviewData, setCancelPreviewData] = useState(null);
    const [cancelClassId, setCancelClassId] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [cancelRefunds, setCancelRefunds] = useState([]); // [{ enrolmentId, refundAmount }]
    const [isLoadingCancelPreview, setIsLoadingCancelPreview] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    const loadTerms = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await classApi.getTerms();
            if (response.ok) {
                const fetchedTerms = response.data.terms || [];
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const sortedTerms = [...fetchedTerms].sort((a, b) => {
                    const aStart = new Date(a.startDate);
                    const aEnd = new Date(a.endDate);
                    const bStart = new Date(b.startDate);
                    const bEnd = new Date(b.endDate);
                    const aIsCurrent = aStart <= today && aEnd >= today;
                    const bIsCurrent = bStart <= today && bEnd >= today;
                    if (aIsCurrent !== bIsCurrent) return aIsCurrent ? -1 : 1;
                    return aStart - bStart; // chronological order within each group
                });
                setTerms(sortedTerms);
                // Start all terms collapsed on mount
                setCollapsedTerms(new Set(fetchedTerms.map(t => t.id)));
                // Load classes for all terms in parallel
                const classResults = await Promise.all(fetchedTerms.map(t => classApi.getClasses(t.id)));
                const map = {};
                fetchedTerms.forEach((t, i) => {
                    map[t.id] = classResults[i].ok ? classResults[i].data.classes || [] : [];
                });
                setClassesMap(map);
            } else {
                toast.error(response.data?.message || "Failed to load terms");
            }
        } catch {
            toast.error("Failed to load terms");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTerms();
    }, [loadTerms]);

    const loadShortCourses = useCallback(async () => {
        try {
            const res = await classApi.getShortCourses();
            if (res.ok) {
                const sorted = [...(res.data.shortCourses || [])].sort((a, b) => {
                    if (!a.startDate && !b.startDate) return 0;
                    if (!a.startDate) return -1;
                    if (!b.startDate) return 1;
                    return new Date(a.startDate) - new Date(b.startDate); // chronological
                });
                setShortCourses(sorted);
            }
        } catch {
            // silent
        }
    }, []);

    useEffect(() => {
        loadShortCourses();
    }, [loadShortCourses]);

    // Auto-calculate end date from start date + duration weeks
    useEffect(() => {
        if (formDurationWeeks && formStartDate) {
            const start = new Date(formStartDate);
            if (!isNaN(start.getTime())) {
                const end = new Date(start);
                end.setDate(end.getDate() + parseInt(formDurationWeeks, 10) * 7 - 1);
                setFormEndDate(end.toISOString().substring(0, 10));
            }
        }
    }, [formDurationWeeks, formStartDate]);

    const refreshClassesForTerm = async termId => {
        try {
            const res = await classApi.getClasses(termId);
            if (res.ok) {
                setClassesMap(prev => ({ ...prev, [termId]: res.data.classes || [] }));
            }
        } catch {
            // silent — pills will just not update
        }
    };

    // --- Term form ---

    const openCreateForm = (defaultType = "term") => {
        if (defaultType === "term") {
            setEditingTerm(null);
            setFormName("");
            setFormType("term");
            setFormStartDate("");
            setFormEndDate("");
            setFormEnrolmentOpen(false);
            setFormDurationWeeks("");
            setImportSourceTermId("");
            setImportCandidateClasses([]);
            setSelectedClassIds(new Set());
            setWizardRolloverSelected(true);
            // Default ON — most rollover flows want students notified so they
            // can confirm. Admin can untick for silent rollover.
            setWizardEmailRollover(true);
            setWizardStep(1);
        } else {
            setEditingTerm(null);
            setFormName("");
            setFormType(defaultType);
            setFormStartDate("");
            setFormEndDate("");
            setFormEnrolmentOpen(false);
            setFormDurationWeeks("");
            setShowForm(true);
        }
    };

    const openEditForm = term => {
        setEditingTerm(term);
        setFormName(term.name);
        setFormType(term.type);
        setFormStartDate(term.startDate ? term.startDate.substring(0, 10) : "");
        setFormEndDate(term.endDate ? term.endDate.substring(0, 10) : "");
        setFormEnrolmentOpen(term.enrolmentOpen || false);
        setFormDurationWeeks("");
        setShowForm(true);
    };

    const handleWizardNext1 = () => {
        if (!formName || !formStartDate || !formEndDate) {
            toast.error("Name, start date and end date are required");
            return;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (new Date(formStartDate) < today) {
            setConfirmModal({
                title: "Backdated term",
                message: "This term starts in the past. Are you sure you meant to backdate it?",
                variant: "warning",
                onConfirm: () => {
                    setConfirmModal(null);
                    setWizardStep(2);
                },
            });
            return;
        }
        setWizardStep(2);
    };

    const handleWizardSubmit = async (skipClasses = false) => {
        setIsWizardSubmitting(true);
        try {
            const termData = {
                name: formName,
                type: "term",
                startDate: formStartDate,
                endDate: formEndDate,
                enrolmentOpen: formEnrolmentOpen,
            };
            const termRes = await classApi.createTerm(termData);
            if (!termRes.ok) {
                toast.error(termRes.data?.message || "Failed to create term");
                return;
            }
            const newTermId = termRes.data.term.id;
            let classCount = 0;
            let rolledOver = 0;
            let copyOk = false;
            let rolloverOk = false;

            if (!skipClasses && importSourceTermId && selectedClassIds.size > 0) {
                const copyRes = await classApi.copyClasses(newTermId, importSourceTermId, Array.from(selectedClassIds));
                if (copyRes.ok) {
                    classCount = copyRes.data.classes.length;
                    copyOk = true;
                } else {
                    toast.error(copyRes.data?.message || "Term created but class copy failed");
                }
            }

            if (copyOk && wizardRolloverSelected && importSourceTermId) {
                const rolloverRes = await classApi.rolloverTerm(importSourceTermId, newTermId);
                if (rolloverRes.ok) {
                    rolledOver = rolloverRes.data.rolledOver || 0;
                    rolloverOk = true;
                } else {
                    toast.error(rolloverRes.data?.message || "Classes copied but student rollover failed");
                }
            }

            setWizardStep(0);
            loadTerms();

            if (rolledOver > 0) {
                toast.success(`Term created, ${classCount} class${classCount !== 1 ? "es" : ""} copied, ${rolledOver} student enrolment${rolledOver !== 1 ? "s" : ""} rolled over`);
            } else if (classCount > 0) {
                toast.success(`Term created with ${classCount} class${classCount !== 1 ? "es" : ""} copied`);
            } else {
                toast.success("Term created");
            }

            if (rolloverOk && wizardEmailRollover) {
                setEmailReviewType('rolloverNotification');
                setEmailReviewData({
                    termName: formName,
                });
                setEmailReviewAudienceDescription(`Rolled-over students in ${formName}`);
                setEmailReviewRecipientCount(rolledOver || null);
                setEmailReviewOnConfirm(() => async (subject, bodyHtml) => {
                    setIsConfirmingSend(true);
                    try {
                        const res = await classApi.sendRolloverNotifications(newTermId, subject, bodyHtml);
                        if (res.ok) {
                            const queued = res.data.notificationsQueued;
                            if (queued === 0) {
                                toast.info('No re-enrolment emails sent — no eligible students found');
                            } else {
                                toast.success(queued != null
                                    ? `Re-enrolment emails sending to ${queued} student${queued !== 1 ? "s" : ""}`
                                    : "Re-enrolment emails are sending in the background"
                                );
                            }
                        } else {
                            toast.error(res.data?.message || "Failed to send emails");
                        }
                    } catch {
                        toast.error("Failed to send emails");
                    } finally {
                        setIsConfirmingSend(false);
                        setShowEmailReviewModal(false);
                    }
                });
                setShowEmailReviewModal(true);
            }
        } catch {
            toast.error("Failed to create term");
        } finally {
            setIsWizardSubmitting(false);
        }
    };

    const doSaveTerm = async () => {
        setIsSaving(true);
        try {
            const data = {
                name: formName,
                type: formType,
                startDate: formStartDate,
                endDate: formEndDate,
                enrolmentOpen: formEnrolmentOpen,
            };
            const response = editingTerm
                ? await classApi.updateTerm(editingTerm.id, data)
                : await classApi.createTerm(data);

            if (response.ok) {
                toast.success(`${typeLabel[formType] || "Term"} updated`);
                setShowForm(false);
                loadTerms();
            } else {
                toast.error(response.data?.message || "Failed to save");
            }
        } catch {
            toast.error("Failed to save");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = () => {
        if (!formName || !formStartDate || !formEndDate) {
            toast.error("Name, start date and end date are required");
            return;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (new Date(formStartDate) < today) {
            setConfirmModal({
                title: "Backdated term",
                message: "This term starts in the past. Are you sure you meant to backdate it?",
                variant: "warning",
                onConfirm: () => {
                    setConfirmModal(null);
                    doSaveTerm();
                },
            });
            return;
        }
        doSaveTerm();
    };

    const handleDelete = term => {
        setConfirmModal({
            title: 'Delete Term',
            message: `Delete "${term.name}"? This cannot be undone.`,
            variant: 'danger',
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    const response = await classApi.deleteTerm(term.id);
                    if (response.ok) {
                        toast.success("Term deleted");
                        loadTerms();
                    } else {
                        toast.error(response.data?.message || "Cannot delete term");
                    }
                } catch {
                    toast.error("Failed to delete term");
                }
            },
        });
    };

    const handleOpenEnrolment = async term => {
        try {
            const response = await classApi.openEnrolment(term.id);
            if (response.ok) {
                toast.success(`Enrolment opened for ${term.name}`);
                loadTerms();
                setEnrolmentReminderTermId(term.id);
                setEnrolmentReminderContext('open');
                setShowEnrolmentReminderModal(true);
            } else {
                toast.error(response.data?.message || "Failed to open enrolment");
            }
        } catch {
            toast.error("Failed to open enrolment");
        }
    };

    const handleCloseEnrolment = async term => {
        try {
            const response = await classApi.updateTerm(term.id, { enrolmentOpen: false });
            if (response.ok) {
                toast.success(`Enrolment closed for ${term.name}`);
                loadTerms();
            } else {
                toast.error(response.data?.message || "Failed to close enrolment");
            }
        } catch {
            toast.error("Failed to close enrolment");
        }
    };

    // --- Import modal handlers ---

    const handleImportSourceChange = async sourceTermId => {
        setImportSourceTermId(sourceTermId);
        if (!sourceTermId) {
            setImportCandidateClasses([]);
            setSelectedClassIds(new Set());
            return;
        }
        setIsLoadingImportClasses(true);
        try {
            const res = await classApi.getClasses(sourceTermId);
            if (res.ok) {
                const classes = res.data.classes || [];
                setImportCandidateClasses(classes);
                setSelectedClassIds(new Set(classes.map(c => c.id)));
            } else {
                toast.error("Failed to load classes from selected term");
            }
        } catch {
            toast.error("Failed to load classes from selected term");
        } finally {
            setIsLoadingImportClasses(false);
        }
    };

    const toggleImportClass = classId => {
        setSelectedClassIds(prev => {
            const next = new Set(prev);
            next.has(classId) ? next.delete(classId) : next.add(classId);
            return next;
        });
    };

    const openPaymentReminderEmailReview = (explicitTermId) => {
        // Accepts an explicit termId so callers can skip the
        // setEnrolmentReminderTermId → setShowEnrolmentReminderModal → click-Yes
        // round-trip. Falls back to the prompt's stored value when called
        // from the prompt's "Yes, send reminders" button.
        const termId = explicitTermId || enrolmentReminderTermId;
        const term = terms.find(t => t.id === termId);
        const unpaidCount = term?.confirmedUnpaidCount || 0;
        setShowEnrolmentReminderModal(false);
        setEnrolmentReminderTermId(null);
        setEmailReviewType('classPayment');
        setEmailReviewData({
            termName: term?.name || '',
            classNames: (classesMap[termId] || []).filter(c => c.isActive !== false).map(c => c.name),
        });
        setEmailReviewAudienceDescription(`Confirmed but unpaid students in ${term?.name || 'this term'}`);
        setEmailReviewRecipientCount(unpaidCount);
        setEmailReviewOnConfirm(() => async (subject, bodyHtml) => {
            setIsConfirmingSend(true);
            try {
                const res = await classApi.sendEnrolmentReminders(termId, subject, bodyHtml);
                if (res.ok) {
                    const sent = res.data.emailsSent ?? 0;
                    const skipped = res.data.emailsSkipped ?? 0;
                    if (sent === 0) {
                        // Distinguish "no eligible recipients" from "send failed" — the
                        // request succeeded but no one matched (already paid, no email,
                        // QR token couldn't be generated, etc).
                        toast.info(skipped > 0
                            ? `No reminders sent — ${skipped} student${skipped !== 1 ? 's' : ''} skipped (already paid or no contact email)`
                            : `No reminders sent — no unpaid students found`);
                    } else {
                        toast.success(`Sending payment reminders to ${sent} student${sent !== 1 ? "s" : ""}${skipped > 0 ? ` (${skipped} skipped)` : ""}`);
                    }
                } else {
                    toast.error(res.data?.message || "Failed to send reminders");
                }
            } catch {
                toast.error("Failed to send reminders");
            } finally {
                setIsConfirmingSend(false);
                setShowEmailReviewModal(false);
            }
        });
        setShowEmailReviewModal(true);
    };

    // --- Class modal ---

    const openClassModal = (termId, cls = null) => {
        setClassModalTermId(termId);
        setEditingClass(cls);
        setClassFormName(cls ? cls.name || "" : "");
        setClassFormStyle(cls ? resolveToId(cls.style) || cls.style || "" : "");
        setClassFormDay(cls ? cls.dayOfWeek : 1);
        setClassFormStartTime(cls ? cls.startTime || "19:00" : "19:00");
        setClassFormEndTime(cls ? cls.endTime || "20:00" : "20:00");
        setClassFormLocation(cls ? cls.roomName || "" : "");
        setClassFormMaxStudents(cls && cls.maxStudents ? String(cls.maxStudents) : "");
        setClassFormDescription(cls ? cls.description || "" : "");
        setShowClassModal(true);
    };

    const handleClassSave = async () => {
        if (!classFormName || !classFormDay || !classFormStartTime || !classFormEndTime) {
            toast.error("Name, day, start time and end time are required");
            return;
        }
        if (classFormEndTime <= classFormStartTime) {
            toast.error("End time must be after start time");
            return;
        }
        setIsClassSaving(true);
        try {
            const data = {
                termId: classModalTermId,
                name: classFormName,
                style: classFormStyle,
                dayOfWeek: classFormDay,
                startTime: classFormStartTime,
                endTime: classFormEndTime,
                roomName: classFormLocation,
                maxStudents: classFormMaxStudents ? parseInt(classFormMaxStudents, 10) : undefined,
                description: classFormDescription,
            };
            const response = editingClass
                ? await classApi.updateClass(editingClass.id, data)
                : await classApi.createClass(data);

            if (response.ok) {
                toast.success(editingClass ? "Class updated" : "Class created");
                setShowClassModal(false);
                refreshClassesForTerm(classModalTermId);
            } else {
                toast.error(response.data?.message || "Failed to save class");
            }
        } catch {
            toast.error("Failed to save class");
        } finally {
            setIsClassSaving(false);
        }
    };

    const handleClassDelete = () => {
        if (!editingClass) return;
        setConfirmModal({
            title: 'Delete Class',
            message: `Delete "${editingClass.name}"? This cannot be undone.`,
            variant: 'danger',
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    const response = await classApi.deleteClass(editingClass.id);
                    if (response.ok) {
                        toast.success("Class deleted");
                        setShowClassModal(false);
                        refreshClassesForTerm(classModalTermId);
                    } else {
                        toast.error(response.data?.message || "Cannot delete class");
                    }
                } catch {
                    toast.error("Failed to delete class");
                }
            },
        });
    };

    // --- Class cancellation (B20) ---

    const openCancelModal = async (cls) => {
        setCancelClassId(cls.id);
        setCancelPreviewData(null);
        setCancelReason("");
        setCancelRefunds([]);
        setShowCancelModal(true);
        setIsLoadingCancelPreview(true);
        try {
            const response = await classApi.cancelClassPreview(cls.id);
            if (response.ok) {
                setCancelPreviewData(response.data);
                setCancelRefunds(response.data.refunds.map(r => ({
                    enrolmentId: r.enrolmentId,
                    refundAmount: r.suggestedRefund,
                    maxAmount: r.amountPaid,
                    studentName: r.studentName,
                    amountPaid: r.amountPaid,
                    currency: r.currency,
                })));
            } else {
                toast.error(response.data?.message || "Failed to load cancellation preview");
                setShowCancelModal(false);
            }
        } catch {
            toast.error("Failed to load cancellation preview");
            setShowCancelModal(false);
        } finally {
            setIsLoadingCancelPreview(false);
        }
    };

    const updateCancelRefundAmount = (enrolmentId, value) => {
        setCancelRefunds(prev => prev.map(r =>
            r.enrolmentId === enrolmentId
                ? { ...r, refundAmount: Math.min(parseFloat(value) || 0, r.maxAmount) }
                : r
        ));
    };

    const handleConfirmCancel = async () => {
        setIsCancelling(true);
        try {
            const refundsPayload = cancelRefunds.map(r => ({
                enrolmentId: r.enrolmentId,
                refundAmount: r.refundAmount,
            }));
            const response = await classApi.cancelClass(cancelClassId, cancelReason, refundsPayload);
            if (response.ok) {
                const totalRefunded = cancelRefunds.reduce((sum, r) => sum + r.refundAmount, 0);
                const refundCount = cancelRefunds.filter(r => r.refundAmount > 0).length;
                toast.success(`Class cancelled. ${refundCount} refund(s) totalling ${formatCurrencyFromMinor(totalRefunded)} issued.`);
                setShowCancelModal(false);
                setShowClassModal(false);
                // Reload the term's classes
                if (classModalTermId) refreshClassesForTerm(classModalTermId);
                loadTerms();
            } else {
                toast.error(response.data?.message || "Failed to cancel class");
            }
        } catch {
            toast.error("Failed to cancel class");
        } finally {
            setIsCancelling(false);
        }
    };

    // --- Short Course modal ---

    const openCreateShortCourseModal = (presetGroupId = "") => {
        setEditingShortCourse(null);
        setScName("");
        setScStyle("");
        setScLevel("");
        setScStartDate("");
        setScSessionCount(4);
        setScStartTime("19:00");
        setScEndTime("20:00");
        setScRequiresApproval(true);
        setScShortCourseGroupId(presetGroupId);
        setScGuestInstructorName("");
        setShowShortCourseModal(true);
    };

    // Auto-open short course modal when navigated from ShortCourseGroupPage
    useEffect(() => {
        if (location.state?.openShortCourse) {
            openCreateShortCourseModal(location.state.groupId || "");
            // Clear state so refresh doesn't re-open
            window.history.replaceState({}, document.title);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openEditShortCourseModal = sc => {
        setEditingShortCourse(sc);
        setScName(sc.name || "");
        setScStyle(resolveToId(sc.style) || sc.style || "");
        setScLevel(sc.level || "");
        setScStartDate(sc.startDate ? sc.startDate.substring(0, 10) : "");
        setScSessionCount(sc.sessionCount || 4);
        setScStartTime(sc.startTime || "19:00");
        setScEndTime(sc.endTime || "20:00");
        setScRequiresApproval(sc.requiresApproval !== false);
        setScShortCourseGroupId(sc.shortCourseGroupId || "");
        setScGuestInstructorName(sc.guestInstructorName || "");
        setShowShortCourseModal(true);
    };

    const handleRerun = sc => {
        setEditingShortCourse(null); // creating new, not editing
        setScName(sc.name || "");
        setScStyle(resolveToId(sc.style) || sc.style || "");
        setScLevel(sc.level || "");
        setScSessionCount(sc.sessionCount || 4);
        setScStartTime(sc.startTime || "19:00");
        setScEndTime(sc.endTime || "20:00");
        setScRequiresApproval(sc.requiresApproval !== false);
        setScStartDate(""); // blank — admin must pick new date
        setScGuestInstructorName(sc.guestInstructorName || "");
        setShowShortCourseModal(true);
    };

    const handleShortCourseSave = async () => {
        if (!scName || !scStartDate || !scStartTime || !scEndTime) {
            toast.error("Name, start date, start time and end time are required");
            return;
        }
        if (scEndTime <= scStartTime) {
            toast.error("End time must be after start time");
            return;
        }
        setIsScSaving(true);
        try {
            const data = {
                name: scName,
                style: scStyle,
                level: scLevel,
                startDate: scStartDate,
                sessionCount: scSessionCount,
                startTime: scStartTime,
                endTime: scEndTime,
                requiresApproval: scRequiresApproval,
                shortCourseGroupId: scShortCourseGroupId || null,
                guestInstructorName: scGuestInstructorName || '',
            };
            const res = editingShortCourse
                ? await classApi.updateShortCourse(editingShortCourse.id, data)
                : await classApi.createShortCourse(data);
            if (res.ok) {
                toast.success(editingShortCourse ? "Short course updated" : "Short course created");
                setShowShortCourseModal(false);
                loadShortCourses();
            } else {
                toast.error(res.data?.message || "Failed to save short course");
            }
        } catch {
            toast.error("Failed to save short course");
        } finally {
            setIsScSaving(false);
        }
    };

    const handleDeleteShortCourse = (id, name) => {
        setConfirmModal({
            title: 'Delete Short Course',
            message: `Delete "${name}"? This cannot be undone.`,
            variant: 'danger',
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    const res = await classApi.deleteShortCourse(id);
                    if (res.ok) {
                        toast.success("Short course deleted");
                        loadShortCourses();
                    } else {
                        toast.error(res.data?.message || "Cannot delete short course");
                    }
                } catch {
                    toast.error("Failed to delete short course");
                }
            },
        });
    };

    // --- Short Course Group handlers ---

    const loadShortCourseGroups = useCallback(async () => {
        try {
            const res = await classApi.getShortCourseGroups();
            if (res.ok) {
                const sorted = [...(res.data.shortCourseGroups || [])].sort((a, b) =>
                    (a.name || '').localeCompare(b.name || '')
                );
                setShortCourseGroups(sorted);
            }
        } catch {
            // silent
        }
    }, []);

    useEffect(() => {
        loadShortCourseGroups();
    }, [loadShortCourseGroups]);

    const openCreateGroupModal = () => {
        setEditingGroup(null);
        setGrpName(""); setGrpLabel(""); setGrpDescription("");
        setGrpStartDate(""); setGrpEndDate(""); setGrpLocation("");
        setShowGroupModal(true);
    };

    const openEditGroupModal = g => {
        setEditingGroup(g);
        setGrpName(g.name || "");
        setGrpLabel(g.label || "");
        setGrpDescription(g.description || "");
        setGrpStartDate(g.startDate ? g.startDate.substring(0, 10) : "");
        setGrpEndDate(g.endDate ? g.endDate.substring(0, 10) : "");
        setGrpLocation(g.location || "");
        setShowGroupModal(true);
    };

    const handleGroupSave = async () => {
        if (!grpName) { toast.error("Name is required"); return; }
        setIsGrpSaving(true);
        try {
            const data = {
                name: grpName,
                label: grpLabel,
                description: grpDescription,
                startDate: grpStartDate || null,
                endDate: grpEndDate || null,
                location: grpLocation,
            };
            const res = editingGroup
                ? await classApi.updateShortCourseGroup(editingGroup.id, data)
                : await classApi.createShortCourseGroup(data);
            if (res.ok) {
                toast.success(editingGroup ? "Group updated" : "Group created");
                setShowGroupModal(false);
                loadShortCourseGroups();
            } else {
                toast.error(res.data?.message || "Failed to save group");
            }
        } catch {
            toast.error("Failed to save group");
        } finally {
            setIsGrpSaving(false);
        }
    };

    const handleDeleteGroup = (id, name) => {
        setConfirmModal({
            title: 'Delete Group',
            message: `Delete "${name}"? This cannot be undone.`,
            variant: 'danger',
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    const res = await classApi.deleteShortCourseGroup(id);
                    if (res.ok) {
                        toast.success("Group deleted");
                        loadShortCourseGroups();
                    } else {
                        toast.error(res.data?.message || "Cannot delete group — remove courses from it first");
                    }
                } catch {
                    toast.error("Failed to delete group");
                }
            },
        });
    };

    // --- Helpers ---

    const formatDate = iso => {
        if (!iso) return "—";
        return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
    };

    const typeLabel = { term: "Term", "short-course": "Short Course", ongoing: "Ongoing" };

    return (
        <>
        <div className="dashboard-container org-console">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button
                                onClick={() => navigate("/classes/manage")}
                                className="back-nav-btn">
                                ← Manage Classes
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon
                                    path={mdiCalendarMultiple}
                                    size={0.9}
                                    color="#451f78"
                                    style={{ marginRight: 8, verticalAlign: "middle" }}
                                />
                                Terms & Classes
                            </h1>
                        </div>
                        <div className="classes-subtitle-row">
                            <p className="sub-portal-header-subtitle">
                                Create a term to organise your weekly classes, then add classes.<br />
                                For standalone intensive workshops, use Short Courses instead.
                            </p>
                            <button
                                className="classes-help-trigger"
                                onClick={() => setHelpOpen(true)}
                                onMouseEnter={() => setHelpIconHovered(true)}
                                onMouseLeave={() => setHelpIconHovered(false)}
                                aria-label="Terms & Classes Help"
                                title="How It Works">
                                <Icon
                                    path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                    size="1.62rem"
                                    color="var(--dl-color-purple-default)"
                                />
                            </button>
                        </div>
                    </div>

                    {/* Split terms and short courses into current vs past */}
                    {(() => {
                            const currentTerms = terms.filter(t => !t.isPast);
                            const pastTerms = terms.filter(t => t.isPast);

                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const groupMap = Object.fromEntries(shortCourseGroups.map(g => [g.id, g]));
                            const activeShortCourses = shortCourses.filter(
                                sc => !sc.endDate || new Date(sc.endDate) >= today
                            );
                            const pastShortCourses = shortCourses.filter(
                                sc => sc.endDate && new Date(sc.endDate) < today
                            );
                            const activeGroups = shortCourseGroups.filter(
                                g => !g.endDate || new Date(g.endDate) >= today
                            );
                            const pastGroups = shortCourseGroups.filter(
                                g => g.endDate && new Date(g.endDate) < today
                            );
                            const DAY_NAMES = [
                                "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
                            ];
                            const historyCount = pastTerms.length + pastShortCourses.length + pastGroups.length;

                            const renderTermRow = (term, isPastView) => {
                                const allTermClasses = classesMap[term.id] || [];
                                const termClasses = allTermClasses.filter(cls => cls.isActive !== false);
                                const cancelledClasses = allTermClasses.filter(cls => cls.isActive === false);
                                return (
                                    <div key={term.id} className={`class-term-row${isPastView ? " class-term-row--past" : ""}`}>
                                        <div className="class-term-row-top">
                                            <div className="class-term-info">
                                                <div className="class-term-name">
                                                    {term.name}
                                                    <span className="class-term-type-badge">
                                                        {typeLabel[term.type] || term.type}
                                                    </span>
                                                    {isPastView && (
                                                        <span className="class-term-ended-badge">Ended</span>
                                                    )}
                                                    {!isPastView && term.enrolmentOpen && (
                                                        <span className="class-term-open-badge">
                                                            Enrolment Open
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="class-term-dates">
                                                    {formatDate(term.startDate)} – {formatDate(term.endDate)}
                                                    {term.weekCount ? ` · ${term.weekCount} weeks` : ""}
                                                </div>
                                                {term.totalEnrolledCount > 0 && (
                                                    <div className="class-term-enrolment-count">
                                                        {term.totalEnrolledCount} {term.totalEnrolledCount === 1 ? 'student' : 'students'} enrolled
                                                    </div>
                                                )}
                                            </div>
                                            {!isPastView && termClasses.length === 0 && term.type !== "short-course" && (
                                                <span className="class-term-empty-hint">
                                                    Add classes <Icon path={mdiArrowRight} size={1.2} />
                                                </span>
                                            )}
                                            <div className="class-term-row-actions">
                                                {termClasses.length > 0 && (
                                                    <button
                                                        className="class-term-action-btn"
                                                        onClick={() => toggleTermCollapse(term.id)}
                                                        title={
                                                            collapsedTerms.has(term.id)
                                                                ? "Expand classes"
                                                                : "Collapse classes"
                                                        }>
                                                        <Icon
                                                            path={
                                                                collapsedTerms.has(term.id)
                                                                    ? mdiChevronDown
                                                                    : mdiChevronUp
                                                            }
                                                            size={0.85}
                                                        />
                                                    </button>
                                                )}
                                                {!isPastView && term.type !== "short-course" && (
                                                    <button
                                                        className="class-term-action-btn"
                                                        onClick={() => openClassModal(term.id)}
                                                        title="Add class">
                                                        <Icon path={mdiCalendarMultiple} size={0.85} />
                                                    </button>
                                                )}
                                                {!isPastView && !term.enrolmentOpen && (
                                                    <button
                                                        className="class-term-action-btn class-term-action-btn--enrolment-closed"
                                                        onClick={() => {
                                                            setOpenEnrolmentConfirmTerm(term);
                                                            setShowOpenEnrolmentConfirmModal(true);
                                                        }}
                                                        title="Open enrolment">
                                                        <Icon path={mdiLock} size={0.85} />
                                                    </button>
                                                )}
                                                {!isPastView && term.enrolmentOpen && (
                                                    <button
                                                        className="class-term-action-btn class-term-action-btn--enrolment-open"
                                                        onClick={() => {
                                                            setCloseEnrolmentConfirmTerm(term);
                                                            setShowCloseEnrolmentConfirmModal(true);
                                                        }}
                                                        title="Enrolment open — click to close">
                                                        <Icon path={mdiLockOpen} size={0.85} />
                                                    </button>
                                                )}
                                                {/* Re-enrolment notifications button removed — will be re-exposed via Communications portal PRP */}
                                                {!isPastView && term.confirmedUnpaidCount > 0 && (
                                                    <button
                                                        className="class-term-action-btn"
                                                        onClick={() => openPaymentReminderEmailReview(term.id)}
                                                        title={`Send payment reminders (${term.confirmedUnpaidCount} confirmed but unpaid)`}>
                                                        <Icon path={mdiCreditCardFastOutline} size={0.85} />
                                                    </button>
                                                )}
                                                {!isPastView && (
                                                    <button
                                                        className="class-term-action-btn"
                                                        onClick={() => openEditForm(term)}
                                                        title="Edit term">
                                                        <Icon path={mdiPencil} size={0.85} />
                                                    </button>
                                                )}
                                                {!isPastView && (
                                                    <button
                                                        className="class-term-action-btn danger"
                                                        onClick={() => handleDelete(term)}
                                                        title="Delete term">
                                                        <Icon path={mdiTrashCan} size={0.85} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {termClasses.length > 0 && !collapsedTerms.has(term.id) && (
                                            <div className="class-term-day-grid">
                                                {(() => {
                                                    const allMinutes = termClasses.flatMap(c => [
                                                        termTimeToMinutes(c.startTime),
                                                        termTimeToMinutes(c.endTime),
                                                    ]);
                                                    const gridStartMin =
                                                        Math.floor(Math.min(...allMinutes) / 60) * 60;
                                                    const gridEndMin = Math.ceil(Math.max(...allMinutes) / 60) * 60;
                                                    const gridHeightPx =
                                                        (gridEndMin - gridStartMin) * (TERM_GRID_PX_PER_HOUR / 60);
                                                    const gridHours = Array.from(
                                                        { length: (gridEndMin - gridStartMin) / 60 + 1 },
                                                        (_, i) => gridStartMin / 60 + i
                                                    );

                                                    const calcTop = st =>
                                                        (termTimeToMinutes(st) - gridStartMin) *
                                                        (TERM_GRID_PX_PER_HOUR / 60);
                                                    const calcHeight = (st, et) =>
                                                        Math.max(
                                                            (termTimeToMinutes(et) - termTimeToMinutes(st)) *
                                                                (TERM_GRID_PX_PER_HOUR / 60) -
                                                                6,
                                                            36
                                                        );

                                                    const dayGroups = [];
                                                    DAY_OPTIONS.forEach((day, dayIndex) => {
                                                        const dayClasses = termClasses.filter(
                                                            cls => cls.dayOfWeek === dayIndex
                                                        );
                                                        if (!dayClasses.length) return;
                                                        const byLocation = {};
                                                        dayClasses.forEach(cls => {
                                                            const loc = cls.roomName || "";
                                                            if (!byLocation[loc]) byLocation[loc] = [];
                                                            byLocation[loc].push(cls);
                                                        });
                                                        const cols = Object.keys(byLocation)
                                                            .sort()
                                                            .map(loc => ({
                                                                location: loc,
                                                                classes: byLocation[loc],
                                                            }));
                                                        dayGroups.push({ day, cols });
                                                    });

                                                    return (
                                                        <>
                                                            <div className="class-term-headers-row">
                                                                {dayGroups.map(({ day, cols }) => (
                                                                    <div
                                                                        key={day}
                                                                        className="class-term-day-group"
                                                                        style={{
                                                                            flex: cols.length,
                                                                            minWidth: 160 * cols.length,
                                                                        }}>
                                                                        <div className="class-term-day-header">
                                                                            {day}
                                                                        </div>
                                                                        <div className="class-term-location-row">
                                                                            {cols.map(({ location }) => (
                                                                                <div
                                                                                    key={location}
                                                                                    className="class-term-location-header">
                                                                                    {location || "—"}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            <div className="class-term-body-row">
                                                                <div
                                                                    className="class-term-time-ruler"
                                                                    style={{ height: gridHeightPx }}>
                                                                    {gridHours.map((hour, i) => (
                                                                        <div
                                                                            key={hour}
                                                                            className="class-term-time-label"
                                                                            style={{
                                                                                top: i * TERM_GRID_PX_PER_HOUR,
                                                                            }}>
                                                                            {`${String(hour).padStart(2, "0")}:00`}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {dayGroups.map(({ day, cols }) => (
                                                                    <div
                                                                        key={day}
                                                                        className="class-term-day-body"
                                                                        style={{
                                                                            height: gridHeightPx,
                                                                            flex: cols.length,
                                                                            minWidth: 160 * cols.length,
                                                                        }}>
                                                                        {cols.map(({ location, classes }) => (
                                                                            <div
                                                                                key={location}
                                                                                className="class-term-day-col">
                                                                                {classes.map(cls => (
                                                                                    <div
                                                                                        key={cls.id}
                                                                                        className="class-info-card"
                                                                                        onClick={() =>
                                                                                            openClassModal(
                                                                                                term.id,
                                                                                                cls
                                                                                            )
                                                                                        }
                                                                                        style={{
                                                                                            top: calcTop(
                                                                                                cls.startTime
                                                                                            ),
                                                                                            height: calcHeight(
                                                                                                cls.startTime,
                                                                                                cls.endTime
                                                                                            ),
                                                                                            borderColor:
                                                                                                getDanceStyleColor(
                                                                                                    cls.style || ""
                                                                                                ),
                                                                                            cursor: "pointer",
                                                                                        }}>
                                                                                        <div className="class-info-card-name">
                                                                                            {cls.name}
                                                                                        </div>
                                                                                        <div className="class-info-card-detail">
                                                                                            <span>
                                                                                                {cls.startTime}–
                                                                                                {cls.endTime}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                        {cancelledClasses.length > 0 && !collapsedTerms.has(term.id) && (
                                            <div className="class-term-cancelled-section">
                                                {cancelledClasses.map(cls => (
                                                    <div
                                                        key={cls.id}
                                                        className="class-term-cancelled-row"
                                                        onClick={() => openClassModal(term.id, cls)}>
                                                        <span className="class-cancelled-pill">Cancelled</span>
                                                        <span className="class-term-cancelled-name">{cls.name}</span>
                                                        <span className="class-term-cancelled-detail">
                                                            {DAY_OPTIONS[cls.dayOfWeek]} {cls.startTime}–{cls.endTime}
                                                            {cls.roomName ? ` · ${cls.roomName}` : ''}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            };

                            const renderShortCourseRow = (sc, isPastView) => {
                                const dayName = DAY_NAMES[sc.dayOfWeek] || "—";
                                return (
                                    <div key={sc.id} className={`class-term-row${isPastView ? " class-term-row--past" : ""}${sc.shortCourseGroupId ? " class-term-row--grouped" : ""}`}>
                                        <div className="class-term-row-top">
                                            <div className="class-term-info">
                                                <div className="class-term-name">
                                                    {sc.name}
                                                    {isPastView && <span className="sc-past-badge">Past</span>}
                                                    {sc.style && (
                                                        <span className={isPastView ? "class-def-style-badge" : "class-term-type-badge"} style={isPastView ? { marginLeft: 6 } : undefined}>
                                                            {sc.style}
                                                            {sc.level ? ` · ${sc.level}` : ""}
                                                        </span>
                                                    )}
                                                    {sc.shortCourseGroupId && groupMap[sc.shortCourseGroupId] && (
                                                        <button
                                                            className="sc-group-pill"
                                                            onClick={() => navigate(`/classes/short-course-groups/${sc.shortCourseGroupId}`)}
                                                            title="View group">
                                                            <Icon path={mdiLayersOutline} size={0.6} />
                                                            Part of {groupMap[sc.shortCourseGroupId].name}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="class-term-dates">
                                                    {formatDate(sc.startDate)} – {formatDate(sc.endDate)}
                                                    {sc.sessionCount ? ` · ${sc.sessionCount} sessions` : ""}
                                                    {" · "}
                                                    {dayName} {sc.startTime}–{sc.endTime}
                                                </div>
                                            </div>
                                            <div className="class-term-row-actions">
                                                <button
                                                    className="class-term-action-btn"
                                                    onClick={() => navigate(`/classes/short-courses/${sc.id}`)}
                                                    title="View enrolments">
                                                    <Icon path={mdiAccountGroup} size={0.85} />
                                                </button>
                                                {isPastView ? (
                                                    <button
                                                        className="class-term-action-btn sc-rerun-btn"
                                                        onClick={() => handleRerun(sc)}
                                                        title="Re-run this course">
                                                        <Icon path={mdiRefresh} size={0.85} /> Re-run
                                                    </button>
                                                ) : (
                                                    <>
                                                        <button
                                                            className="class-term-action-btn"
                                                            onClick={() => openEditShortCourseModal(sc)}
                                                            title="Edit short course">
                                                            <Icon path={mdiPencil} size={0.85} />
                                                        </button>
                                                        <button
                                                            className="class-term-action-btn danger"
                                                            onClick={() => handleDeleteShortCourse(sc.id, sc.name)}
                                                            title="Delete short course">
                                                            <Icon path={mdiTrashCan} size={0.85} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            };

                            const termsAndCoursesTab = (
                                <>
                                    <div className="class-terms-actions-bar">
                                        <h3>Terms & Courses</h3>
                                        <div className="class-terms-actions-bar-buttons">
                                            <button className="class-terms-add-btn" onClick={() => openCreateForm("term")}>
                                                <Icon path={mdiPlus} size={0.9} /> New Term
                                            </button>
                                            <button className="class-terms-add-btn turquoise" onClick={() => openCreateShortCourseModal()}>
                                                <Icon path={mdiPlus} size={0.9} /> New Short Course
                                            </button>
                                            <button className="class-terms-add-btn" onClick={openCreateGroupModal}>
                                                <Icon path={mdiPlus} size={0.9} /> New Group
                                            </button>
                                        </div>
                                    </div>

                                    {/* Terms section */}
                                    <div className="class-terms-section-divider">
                                        <span>Terms {!isLoading && `(${currentTerms.length})`}</span>
                                    </div>

                                    {isLoading ? (
                                        <p className="class-terms-loading">Loading terms...</p>
                                    ) : currentTerms.length === 0 ? (
                                        <p className="class-terms-empty">No current terms. Create a new term to get started.</p>
                                    ) : (
                                        <div className="class-terms-list">
                                            {currentTerms.map(term => renderTermRow(term, false))}
                                        </div>
                                    )}

                                    {/* Short Courses section */}
                                    <div className="class-terms-section-divider">
                                        <span>Short Courses ({activeShortCourses.length})</span>
                                    </div>
                                    {activeShortCourses.length === 0 ? (
                                        <p className="class-terms-empty">
                                            {shortCourses.length === 0
                                                ? 'No short courses yet. Click "New Short Course" above to add one.'
                                                : "No active short courses."}
                                        </p>
                                    ) : (
                                        <div className="class-terms-list">
                                            {activeShortCourses.map(sc => renderShortCourseRow(sc, false))}
                                        </div>
                                    )}

                                    {/* Groups section — active groups only */}
                                    <div className="class-terms-section-divider">
                                        <span>Groups ({activeGroups.length})</span>
                                    </div>
                                    {activeGroups.length === 0 ? (
                                        <p className="class-terms-empty">
                                            No groups yet. Click "New Group" above to create a Weekender, Congress, or Festival container.
                                        </p>
                                    ) : (
                                        <div className="class-terms-list">
                                            {activeGroups.map(g => (
                                                <div key={g.id} className="class-term-row">
                                                    <div className="class-term-row-top">
                                                        <div className="class-term-info">
                                                            <div className="class-term-name">
                                                                {g.name}
                                                                {g.label && (
                                                                    <span className="class-term-type-badge">{g.label}</span>
                                                                )}
                                                            </div>
                                                            <div className="class-term-dates">
                                                                {g.startDate || g.endDate
                                                                    ? `${formatDate(g.startDate)} – ${formatDate(g.endDate)}`
                                                                    : "No dates set"}
                                                                {g.location ? ` · ${g.location}` : ""}
                                                            </div>
                                                        </div>
                                                        <div className="class-term-row-actions">
                                                            <button
                                                                className="class-term-action-btn"
                                                                onClick={() => navigate(`/classes/short-course-groups/${g.id}`)}
                                                                title="View courses in this group">
                                                                <Icon path={mdiLayersOutline} size={0.85} />
                                                            </button>
                                                            <button
                                                                className="class-term-action-btn"
                                                                onClick={() => openEditGroupModal(g)}
                                                                title="Edit group">
                                                                <Icon path={mdiPencil} size={0.85} />
                                                            </button>
                                                            <button
                                                                className="class-term-action-btn danger"
                                                                onClick={() => handleDeleteGroup(g.id, g.name)}
                                                                title="Delete group">
                                                                <Icon path={mdiTrashCan} size={0.85} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            );

                            const historyTab = (
                                <>
                                    {historyCount === 0 ? (
                                        <p className="class-terms-empty">No past terms or courses yet.</p>
                                    ) : (
                                        <>
                                            {/* Past Terms */}
                                            {pastTerms.length > 0 && (
                                                <>
                                                    <div className="class-terms-section-divider">
                                                        <span>Past Terms ({pastTerms.length})</span>
                                                    </div>
                                                    <div className="class-terms-list">
                                                        {pastTerms.map(term => renderTermRow(term, true))}
                                                    </div>
                                                </>
                                            )}

                                            {/* Past Short Courses */}
                                            {pastShortCourses.length > 0 && (
                                                <>
                                                    <div className="class-terms-section-divider">
                                                        <span>Past Short Courses ({pastShortCourses.length})</span>
                                                    </div>
                                                    <div className="class-terms-list">
                                                        {pastShortCourses.map(sc => renderShortCourseRow(sc, true))}
                                                    </div>
                                                </>
                                            )}

                                            {/* Past Groups */}
                                            {pastGroups.length > 0 && (
                                                <>
                                                    <div className="class-terms-section-divider">
                                                        <span>Past Groups ({pastGroups.length})</span>
                                                    </div>
                                                    <div className="class-terms-list">
                                                        {pastGroups.map(g => (
                                                            <div key={g.id} className="class-term-row class-term-row--past">
                                                                <div className="class-term-row-top">
                                                                    <div className="class-term-info">
                                                                        <div className="class-term-name">
                                                                            {g.name}
                                                                            {g.label && (
                                                                                <span className="class-term-type-badge">{g.label}</span>
                                                                            )}
                                                                            <span className="class-term-ended-badge">Ended</span>
                                                                        </div>
                                                                        <div className="class-term-dates">
                                                                            {g.startDate || g.endDate
                                                                                ? `${formatDate(g.startDate)} – ${formatDate(g.endDate)}`
                                                                                : "No dates set"}
                                                                            {g.location ? ` · ${g.location}` : ""}
                                                                        </div>
                                                                    </div>
                                                                    <div className="class-term-row-actions">
                                                                        <button
                                                                            className="class-term-action-btn"
                                                                            onClick={() => navigate(`/classes/short-course-groups/${g.id}`)}
                                                                            title="View courses in this group">
                                                                            <Icon path={mdiLayersOutline} size={0.85} />
                                                                        </button>
                                                                        <button
                                                                            className="class-term-action-btn danger"
                                                                            onClick={() => handleDeleteGroup(g.id, g.name)}
                                                                            title="Delete group">
                                                                            <Icon path={mdiTrashCan} size={0.85} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            );

                            const tabs = [
                                { label: "Terms & Courses", content: termsAndCoursesTab },
                                { label: "History", badge: historyCount, content: historyTab },
                            ];

                            return <FolderTabs tabs={tabs} defaultTab={0} />;
                        })()}

                    {/* Short Course modal */}
                    {showShortCourseModal && (
                        <div className="modal-overlay" onClick={() => setShowShortCourseModal(false)}>
                            <div className="class-terms-modal-content" onClick={e => e.stopPropagation()}>
                                <div className="class-terms-modal-header">
                                    <h2>
                                        <Icon
                                            path={mdiSchool}
                                            size={0.9}
                                            color="#451f78"
                                            style={{ marginRight: 8, verticalAlign: "middle" }}
                                        />
                                        {editingShortCourse ? "Edit Short Course" : "New Short Course"}
                                    </h2>
                                    <button
                                        className="class-terms-modal-close"
                                        onClick={() => setShowShortCourseModal(false)}>
                                        ×
                                    </button>
                                </div>
                                <div className="class-terms-modal-body">
                                    <label className="class-term-form-label">Name *</label>
                                    <input
                                        className="class-term-form-input"
                                        value={scName}
                                        onChange={e => setScName(e.target.value)}
                                        placeholder="e.g. Salsa Masterclass March 2026"
                                    />

                                    <label className="class-term-form-label">Style</label>
                                    <select
                                        className="class-term-form-input"
                                        value={scStyle}
                                        onChange={e => setScStyle(e.target.value)}>
                                        <option value="">— Select style —</option>
                                        {getDanceStyles().map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.label}
                                            </option>
                                        ))}
                                    </select>

                                    <label className="class-term-form-label">Level</label>
                                    <input
                                        className="class-term-form-input"
                                        value={scLevel}
                                        onChange={e => setScLevel(e.target.value)}
                                        placeholder="e.g. All Levels"
                                    />

                                    <label className="class-term-form-label">Start Date *</label>
                                    <p className="class-term-form-hint">
                                        Day of week is derived automatically from this date.
                                    </p>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <DateInputWithPicker
                                            value={scStartDate}
                                            onChange={isoDate => setScStartDate(isoDate)}
                                        />
                                        {scStartDate && (
                                            <span style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                padding: "6px 14px",
                                                borderRadius: 8,
                                                background: "rgba(69, 31, 120, 0.1)",
                                                border: "1px solid rgba(69, 31, 120, 0.25)",
                                                color: "#451f78",
                                                fontWeight: 700,
                                                fontSize: "0.92rem",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                            }}>
                                                {
                                                    [
                                                        "Sunday",
                                                        "Monday",
                                                        "Tuesday",
                                                        "Wednesday",
                                                        "Thursday",
                                                        "Friday",
                                                        "Saturday",
                                                    ][new Date(scStartDate).getDay()]
                                                }
                                            </span>
                                        )}
                                    </div>

                                    <label className="class-term-form-label">Number of Sessions</label>
                                    <input
                                        type="number"
                                        className="class-term-form-input"
                                        value={scSessionCount}
                                        onChange={e => setScSessionCount(Number(e.target.value))}
                                        min="1"
                                        placeholder="e.g. 4"
                                    />

                                    <div className="class-terms-form-row">
                                        <div>
                                            <label className="class-term-form-label">Start Time *</label>
                                            <input
                                                type="time"
                                                className="class-term-form-input"
                                                value={scStartTime}
                                                onChange={e => {
                                                    const newStart = e.target.value;
                                                    setScStartTime(newStart);
                                                    if (newStart >= scEndTime) {
                                                        setScEndTime(addOneHour(newStart));
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="class-term-form-label">End Time *</label>
                                            <input
                                                type="time"
                                                className="class-term-form-input"
                                                value={scEndTime}
                                                onChange={e => setScEndTime(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <label className="class-term-form-checkbox-label" style={{ marginTop: 14 }}>
                                        <input
                                            type="checkbox"
                                            checked={scRequiresApproval}
                                            onChange={e => setScRequiresApproval(e.target.checked)}
                                        />
                                        Requires vetting before enrolment
                                    </label>
                                    <p className="class-term-form-hint">
                                        Students reserve a spot; you approve them before payment is requested.
                                    </p>

                                    <label className="class-term-form-label" style={{ marginTop: 14 }}>Guest instructor name (optional)</label>
                                    <p className="class-term-form-hint" style={{ marginBottom: 6 }}>
                                        For visiting artists who don't have an app account. Leave blank for school-run courses with assigned instructors.
                                    </p>
                                    <input
                                        type="text"
                                        className="class-term-form-input"
                                        placeholder="e.g. Juan Martinez"
                                        value={scGuestInstructorName}
                                        onChange={e => setScGuestInstructorName(e.target.value)}
                                    />

                                    {shortCourseGroups.length > 0 && (
                                        <>
                                            <label className="class-term-form-label" style={{ marginTop: 14 }}>Group (optional)</label>
                                            <select
                                                className="class-term-form-input"
                                                value={scShortCourseGroupId}
                                                onChange={e => setScShortCourseGroupId(e.target.value)}>
                                                <option value="">— No group —</option>
                                                {shortCourseGroups.map(g => (
                                                    <option key={g.id} value={g.id}>{g.name}</option>
                                                ))}
                                            </select>
                                        </>
                                    )}
                                </div>
                                <div className="class-terms-modal-footer">
                                    <button
                                        className="class-term-form-cancel"
                                        onClick={() => setShowShortCourseModal(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        className="class-term-form-save"
                                        onClick={handleShortCourseSave}
                                        disabled={isScSaving}>
                                        {isScSaving
                                            ? "Saving..."
                                            : editingShortCourse
                                              ? "Save Changes"
                                              : "Create Short Course"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Term form modal */}
                    {showForm && (
                        <div className="modal-overlay" onClick={() => setShowForm(false)}>
                            <div className="class-terms-modal-content" onClick={e => e.stopPropagation()}>
                                <div className="class-terms-modal-header">
                                    <h2>
                                        <Icon
                                            path={mdiCalendarMultiple}
                                            size={0.9}
                                            color="#451f78"
                                            style={{ marginRight: 8, verticalAlign: "middle" }}
                                        />
                                        {editingTerm
                                            ? `Edit ${typeLabel[editingTerm.type] || "Term"}`
                                            : typeLabel[formType] || "New Term"}
                                    </h2>
                                    <button className="class-terms-modal-close" onClick={() => setShowForm(false)}>
                                        ×
                                    </button>
                                </div>
                                <div className="class-terms-modal-body">
                                    <label className="class-term-form-label">Name *</label>
                                    <input
                                        className="class-term-form-input"
                                        value={formName}
                                        onChange={e => setFormName(e.target.value)}
                                        placeholder="e.g. Term 1 2026"
                                    />

                                    {editingTerm && (
                                        <>
                                            <label className="class-term-form-label">Type</label>
                                            <div className="class-term-form-readonly">
                                                <span className="class-term-type-badge">
                                                    {typeLabel[formType] || formType}
                                                </span>
                                            </div>
                                        </>
                                    )}

                                    <label className="class-term-form-label">Start Date *</label>
                                    <DateInputWithPicker
                                        value={formStartDate}
                                        onChange={isoDate => setFormStartDate(isoDate)}
                                        disablePast={false}
                                    />

                                    <label className="class-term-form-label">Duration</label>
                                    <select
                                        className="class-term-form-input"
                                        value={formDurationWeeks}
                                        onChange={e => setFormDurationWeeks(e.target.value)}>
                                        <option value="">— Set end date manually —</option>
                                        <option value="1">1 week</option>
                                        <option value="2">2 weeks</option>
                                        <option value="3">3 weeks</option>
                                        <option value="4">4 weeks</option>
                                        <option value="5">5 weeks</option>
                                        <option value="6">6 weeks</option>
                                        <option value="7">7 weeks</option>
                                        <option value="8">8 weeks</option>
                                        <option value="10">10 weeks</option>
                                        <option value="12">12 weeks</option>
                                        <option value="16">16 weeks</option>
                                    </select>

                                    <label className="class-term-form-label">
                                        End Date *{formDurationWeeks ? " (auto-calculated)" : ""}
                                    </label>
                                    <DateInputWithPicker
                                        value={formEndDate}
                                        onChange={isoDate => {
                                            setFormDurationWeeks("");
                                            setFormEndDate(isoDate);
                                        }}
                                        style={formDurationWeeks ? { opacity: 0.6, pointerEvents: "none" } : {}}
                                    />

                                    <label className="class-term-form-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formEnrolmentOpen}
                                            onChange={e => setFormEnrolmentOpen(e.target.checked)}
                                        />
                                        Enrolment open
                                    </label>

                                    {/* Overlap warning — advisory only */}
                                    {formStartDate && formEndDate && (() => {
                                        const newStart = new Date(formStartDate);
                                        const newEnd = new Date(formEndDate);
                                        const overlapping = terms.filter(t => {
                                            if (editingTerm && t.id === editingTerm.id) return false;
                                            const tStart = new Date(t.startDate);
                                            const tEnd = new Date(t.endDate);
                                            return newStart <= tEnd && newEnd >= tStart;
                                        });
                                        if (overlapping.length === 0) return null;
                                        return (
                                            <p className="class-term-overlap-warning">
                                                ⚠ Dates overlap with: {overlapping.map(t => t.name).join(', ')}. That's fine if they cover different class styles.
                                            </p>
                                        );
                                    })()}
                                </div>
                                <div className="class-terms-modal-footer">
                                    <button className="class-term-form-cancel" onClick={() => setShowForm(false)}>
                                        Cancel
                                    </button>
                                    <button className="class-term-form-save" onClick={handleSave} disabled={isSaving}>
                                        {isSaving ? "Saving..." : `Save ${typeLabel[formType] || "Term"}`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Class modal */}
                    {showClassModal && (
                        <div className="modal-overlay" onClick={() => setShowClassModal(false)}>
                            <div
                                className="class-terms-modal-content class-terms-modal-wide"
                                onClick={e => e.stopPropagation()}>
                                <div className="class-terms-modal-header">
                                    <h2>
                                        <Icon
                                            path={mdiSchool}
                                            size={0.9}
                                            color="#451f78"
                                            style={{ marginRight: 8, verticalAlign: "middle" }}
                                        />
                                        {editingClass ? "Edit Class" : "Add Class"}
                                    </h2>
                                    <button
                                        className="class-terms-modal-close"
                                        onClick={() => setShowClassModal(false)}>
                                        ×
                                    </button>
                                </div>
                                <div className="class-terms-modal-body">
                                    {editingClass &&
                                        (() => {
                                            const modalTerm = terms.find(t => t.id === classModalTermId);
                                            const termStarted =
                                                modalTerm?.startDate && new Date(modalTerm.startDate) <= new Date();
                                            return termStarted ? (
                                                <p
                                                    className="class-term-form-hint"
                                                    style={{ marginTop: 0, marginBottom: 8, color: "#666" }}>
                                                    This term has started. Any changes will be reflected immediately for
                                                    enrolled students.
                                                </p>
                                            ) : null;
                                        })()}
                                    <label className="class-term-form-label">Class Name *</label>
                                    <input
                                        className="class-term-form-input"
                                        value={classFormName}
                                        onChange={e => setClassFormName(e.target.value)}
                                        placeholder="e.g. Bachata Beginners"
                                    />

                                    <label className="class-term-form-label">Style</label>
                                    <select
                                        className="class-term-form-input"
                                        value={classFormStyle}
                                        onChange={e => setClassFormStyle(e.target.value)}>
                                        <option value="">— Select style —</option>
                                        {getDanceStyles().map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.label}
                                            </option>
                                        ))}
                                    </select>

                                    <div className="class-terms-form-row">
                                        <div>
                                            <label className="class-term-form-label">Day *</label>
                                            <select
                                                className="class-term-form-input"
                                                value={classFormDay}
                                                onChange={e => setClassFormDay(Number(e.target.value))}>
                                                {DAY_OPTIONS.map((d, i) => (
                                                    <option key={i} value={i}>
                                                        {d}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="class-term-form-label">Start Time *</label>
                                            <input
                                                type="time"
                                                className="class-term-form-input"
                                                value={classFormStartTime}
                                                onChange={e => {
                                                    const newStart = e.target.value;
                                                    setClassFormStartTime(newStart);
                                                    if (newStart >= classFormEndTime) {
                                                        setClassFormEndTime(addOneHour(newStart));
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="class-term-form-label">End Time *</label>
                                            <input
                                                type="time"
                                                className="class-term-form-input"
                                                value={classFormEndTime}
                                                onChange={e => setClassFormEndTime(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <label className="class-term-form-label">Location</label>
                                    <input
                                        className="class-term-form-input"
                                        value={classFormLocation}
                                        onChange={e => setClassFormLocation(e.target.value)}
                                        placeholder="e.g. Studio A"
                                    />

                                    <label className="class-term-form-label">Max Students</label>
                                    <input
                                        type="number"
                                        className="class-term-form-input"
                                        value={classFormMaxStudents}
                                        onChange={e => setClassFormMaxStudents(e.target.value)}
                                        placeholder="Leave blank for unlimited"
                                        min="1"
                                    />

                                    <label className="class-term-form-label">Description</label>
                                    <textarea
                                        className="class-term-form-input class-terms-textarea"
                                        value={classFormDescription}
                                        onChange={e => setClassFormDescription(e.target.value)}
                                        placeholder="Optional class description"
                                        rows={3}
                                    />
                                </div>
                                <div className="class-terms-modal-footer">
                                    {editingClass && (
                                        <>
                                            <button className="class-terms-modal-delete-btn" onClick={handleClassDelete}>
                                                Delete
                                            </button>
                                            {editingClass.isActive !== false && (
                                                <button
                                                    className="class-terms-modal-cancel-class-btn"
                                                    style={{ marginLeft: 4 }}
                                                    onClick={() => openCancelModal(editingClass)}>
                                                    Cancel &amp; Refund
                                                </button>
                                            )}
                                            {editingClass.isActive === false && (
                                                <span className="class-terms-modal-cancelled-label">This class has been cancelled</span>
                                            )}
                                        </>
                                    )}
                                    <button className="class-term-form-cancel" onClick={() => setShowClassModal(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        className="class-term-form-save"
                                        onClick={handleClassSave}
                                        disabled={isClassSaving}>
                                        {isClassSaving ? "Saving..." : editingClass ? "Save Changes" : "Add Class"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* 3-step New Term Wizard */}
                    {wizardStep > 0 && (
                        <div className="modal-overlay" onClick={() => { if (!isWizardSubmitting) setWizardStep(0); }}>
                            <div className="class-terms-modal-content class-import-modal" onClick={e => e.stopPropagation()}>
                                <div className="class-terms-modal-header">
                                    <h2>
                                        <Icon path={mdiCalendarMultiple} size={0.9} color="var(--dl-color-purple-default)"
                                            style={{ marginRight: 8, verticalAlign: "middle" }} />
                                        New Term
                                    </h2>
                                    <button className="class-terms-modal-close" onClick={() => { if (!isWizardSubmitting) setWizardStep(0); }}>×</button>
                                </div>

                                {/* STEP 1: Term Details */}
                                {wizardStep === 1 && (
                                    <>
                                        <div className="class-terms-modal-body">
                                            <p className="term-wizard-progress">Step 1 of 3: Term Details</p>

                                            <label className="class-term-form-label">Name *</label>
                                            <input
                                                className="class-term-form-input"
                                                value={formName}
                                                onChange={e => setFormName(e.target.value)}
                                                placeholder="e.g. Term 1 2026"
                                            />

                                            <label className="class-term-form-label">Start Date *</label>
                                            <DateInputWithPicker
                                                value={formStartDate}
                                                onChange={isoDate => setFormStartDate(isoDate)}
                                                disablePast={false}
                                            />

                                            <label className="class-term-form-label">Duration</label>
                                            <select
                                                className="class-term-form-input"
                                                value={formDurationWeeks}
                                                onChange={e => setFormDurationWeeks(e.target.value)}>
                                                <option value="">- Set end date manually -</option>
                                                <option value="1">1 week</option>
                                                <option value="2">2 weeks</option>
                                                <option value="3">3 weeks</option>
                                                <option value="4">4 weeks</option>
                                                <option value="5">5 weeks</option>
                                                <option value="6">6 weeks</option>
                                                <option value="7">7 weeks</option>
                                                <option value="8">8 weeks</option>
                                                <option value="10">10 weeks</option>
                                                <option value="12">12 weeks</option>
                                                <option value="16">16 weeks</option>
                                            </select>

                                            <label className="class-term-form-label">
                                                End Date *{formDurationWeeks ? " (auto-calculated)" : ""}
                                            </label>
                                            <DateInputWithPicker
                                                value={formEndDate}
                                                onChange={isoDate => {
                                                    setFormDurationWeeks("");
                                                    setFormEndDate(isoDate);
                                                }}
                                                style={formDurationWeeks ? { opacity: 0.6, pointerEvents: "none" } : {}}
                                            />

                                            <label className="class-term-form-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={formEnrolmentOpen}
                                                    onChange={e => setFormEnrolmentOpen(e.target.checked)}
                                                />
                                                Enrolment open
                                            </label>

                                            {formStartDate && formEndDate && (() => {
                                                const newStart = new Date(formStartDate);
                                                const newEnd = new Date(formEndDate);
                                                const overlapping = terms.filter(t => {
                                                    const tStart = new Date(t.startDate);
                                                    const tEnd = new Date(t.endDate);
                                                    return newStart <= tEnd && newEnd >= tStart;
                                                });
                                                if (overlapping.length === 0) return null;
                                                return (
                                                    <p className="class-term-overlap-warning">
                                                        Dates overlap with: {overlapping.map(t => t.name).join(', ')}. That is fine if they cover different class styles.
                                                    </p>
                                                );
                                            })()}
                                        </div>
                                        <div className="class-terms-modal-footer">
                                            <button className="class-term-form-cancel" onClick={() => setWizardStep(0)}>Cancel</button>
                                            <button className="class-term-form-save" onClick={handleWizardNext1}>Next</button>
                                        </div>
                                    </>
                                )}

                                {/* STEP 2: Copy Classes */}
                                {wizardStep === 2 && (
                                    <>
                                        <div className="class-terms-modal-body">
                                            <p className="term-wizard-progress">Step 2 of 3: Copy Classes</p>
                                            <p className="modal-subtitle" style={{ marginTop: 0 }}>
                                                Import classes from a previous term into <strong>{formName}</strong>, or start fresh and add classes manually.
                                            </p>

                                            <div className="import-option-row">
                                                <label className="class-term-form-label">Copy classes from:</label>
                                                <select
                                                    value={importSourceTermId}
                                                    onChange={e => handleImportSourceChange(e.target.value)}
                                                    className="class-term-form-input">
                                                    <option value="">- Select a term -</option>
                                                    {terms
                                                        .filter(t => t.type !== "short-course")
                                                        .map(t => (
                                                            <option key={t.id} value={t.id}>{t.name}</option>
                                                        ))}
                                                </select>
                                            </div>

                                            {isLoadingImportClasses && <p className="loading-text">Loading classes...</p>}

                                            {importCandidateClasses.length > 0 && (
                                                <div className="import-class-list">
                                                    <div className="import-select-all">
                                                        <CustomFormInput
                                                            type="checkbox"
                                                            variant="purple"
                                                            label="Select all"
                                                            checked={selectedClassIds.size === importCandidateClasses.length}
                                                            onChange={e => {
                                                                if (e.target.checked) {
                                                                    setSelectedClassIds(new Set(importCandidateClasses.map(c => c.id)));
                                                                } else {
                                                                    setSelectedClassIds(new Set());
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    {importCandidateClasses.map(cls => (
                                                        <div key={cls.id} className="import-class-row">
                                                            <CustomFormInput
                                                                type="checkbox"
                                                                variant="purple"
                                                                label={`${cls.name} - ${cls.style ? getDanceStyleLabel(cls.style) : ""}${cls.level ? ` ${cls.level}` : ""} - ${DAY_OPTIONS[cls.dayOfWeek] || cls.dayOfWeek} ${cls.startTime}-${cls.endTime}`}
                                                                checked={selectedClassIds.has(cls.id)}
                                                                onChange={() => toggleImportClass(cls.id)}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="class-terms-modal-footer">
                                            <button className="class-term-form-cancel" onClick={() => setWizardStep(1)}>Back</button>
                                            <button
                                                className="class-term-form-cancel"
                                                onClick={() => handleWizardSubmit(true)}
                                                disabled={isWizardSubmitting}>
                                                {isWizardSubmitting ? "Creating..." : "Start Fresh"}
                                            </button>
                                            <button
                                                className="class-term-form-save"
                                                onClick={() => setWizardStep(3)}
                                                disabled={!importSourceTermId || selectedClassIds.size === 0 || isLoadingImportClasses}>
                                                Next
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* STEP 3: Roll Over Students */}
                                {wizardStep === 3 && (
                                    <>
                                        <div className="class-terms-modal-body">
                                            <p className="term-wizard-progress">Step 3 of 3: Roll Over Students</p>
                                            <p className="modal-subtitle" style={{ marginTop: 0 }}>
                                                {selectedClassIds.size} class{selectedClassIds.size !== 1 ? "es" : ""} will be copied from{" "}
                                                <strong>{terms.find(t => t.id === importSourceTermId)?.name || "previous term"}</strong> into{" "}
                                                <strong>{formName}</strong>.
                                            </p>

                                            <div className="term-wizard-class-preview">
                                                {importCandidateClasses
                                                    .filter(c => selectedClassIds.has(c.id))
                                                    .map(cls => (
                                                        <div key={cls.id} className="term-wizard-class-item">
                                                            {cls.name}{cls.style ? ` - ${getDanceStyleLabel(cls.style)}` : ""}{cls.level ? ` ${cls.level}` : ""}
                                                        </div>
                                                    ))}
                                            </div>

                                            <div className="term-wizard-rollover-row">
                                                <CustomFormInput
                                                    type="checkbox"
                                                    variant="green"
                                                    label={`Also roll over confirmed students from ${terms.find(t => t.id === importSourceTermId)?.name || "previous term"}`}
                                                    checked={wizardRolloverSelected}
                                                    onChange={e => {
                                                        setWizardRolloverSelected(e.target.checked);
                                                        if (!e.target.checked) setWizardEmailRollover(false);
                                                    }}
                                                />
                                            </div>
                                            {wizardRolloverSelected && (
                                                <div className="term-wizard-rollover-row">
                                                    <CustomFormInput
                                                        type="checkbox"
                                                        variant="green"
                                                        label="Email each student a one-tap link to confirm their spot"
                                                        checked={wizardEmailRollover}
                                                        onChange={e => setWizardEmailRollover(e.target.checked)}
                                                    />
                                                </div>
                                            )}
                                            <p className="class-term-form-hint">
                                                Rolling over places each student into the new term as <strong>unconfirmed</strong>. The email lets them confirm in one tap from any device. Without it, you'll need to confirm them in person at the first class or by hand from the students page.
                                            </p>
                                        </div>
                                        <div className="class-terms-modal-footer">
                                            <button className="class-term-form-cancel" onClick={() => setWizardStep(2)} disabled={isWizardSubmitting}>Back</button>
                                            <button
                                                className="class-term-form-save"
                                                onClick={() => handleWizardSubmit(false)}
                                                disabled={isWizardSubmitting}>
                                                {isWizardSubmitting ? "Creating..." : "Create Term"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Class Cancellation Modal (B20) */}
                    {showCancelModal && (
                        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
                            <div className="class-terms-modal-content class-terms-modal-wide" onClick={e => e.stopPropagation()}>
                                <div className="class-terms-modal-header">
                                    <h2>
                                        <Icon
                                            path={mdiTrashCan}
                                            size={0.9}
                                            color="#ef4444"
                                            style={{ marginRight: 8, verticalAlign: "middle" }}
                                        />
                                        Cancel Class
                                    </h2>
                                    <button className="class-terms-modal-close" onClick={() => setShowCancelModal(false)}>×</button>
                                </div>
                                <div className="class-terms-modal-body">
                                    {isLoadingCancelPreview ? (
                                        <p className="class-terms-loading">Loading refund preview...</p>
                                    ) : cancelPreviewData ? (
                                        <>
                                            <p className="class-term-form-hint" style={{ marginTop: 0, marginBottom: 16 }}>
                                                <strong>{cancelPreviewData.className}</strong> —{' '}
                                                {cancelPreviewData.enrolmentCount} enrolled student(s).
                                                Review refund amounts before confirming.
                                            </p>

                                            {cancelPreviewData.refunds && cancelPreviewData.refunds.length > 0 ? (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: '0.9rem' }}>
                                                    <thead>
                                                        <tr>
                                                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #eee' }}>Student</th>
                                                            <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '2px solid #eee' }}>Paid</th>
                                                            <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '2px solid #eee' }}>Refund Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {cancelRefunds.map(r => (
                                                            <tr key={r.enrolmentId}>
                                                                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{r.studentName}</td>
                                                                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                                                                    {r.amountPaid > 0 ? formatCurrencyFromMinor(r.amountPaid) : 'No payment'}
                                                                </td>
                                                                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                                                                    {r.amountPaid > 0 ? (
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            max={r.amountPaid / 100}
                                                                            step="1"
                                                                            value={(r.refundAmount / 100).toFixed(2)}
                                                                            onChange={e => updateCancelRefundAmount(r.enrolmentId, parseFloat(e.target.value) * 100)}
                                                                            style={{ width: 80, padding: '4px 6px', border: '1px solid #ddd', borderRadius: 4 }}
                                                                        />
                                                                    ) : (
                                                                        <span style={{ color: '#999', fontSize: '0.85rem' }}>No payment recorded</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <p className="class-term-form-hint" style={{ marginBottom: 16 }}>No enrolled students.</p>
                                            )}

                                            <label className="class-term-form-label">Reason (optional)</label>
                                            <input
                                                type="text"
                                                className="class-term-form-input"
                                                value={cancelReason}
                                                onChange={e => setCancelReason(e.target.value)}
                                                placeholder="e.g. Instructor unavailable"
                                            />
                                        </>
                                    ) : null}
                                </div>
                                {cancelPreviewData && (
                                    <div className="class-terms-modal-footer">
                                        <button className="class-term-form-cancel" onClick={() => setShowCancelModal(false)}>
                                            Back
                                        </button>
                                        <button
                                            className="class-terms-modal-delete-btn"
                                            onClick={handleConfirmCancel}
                                            disabled={isCancelling}>
                                            {isCancelling ? 'Cancelling...' : 'Cancel Class & Issue Refunds'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}



                    {/* Open enrolment confirmation gate */}
                    <BaseModal
                        isOpen={showOpenEnrolmentConfirmModal && !!openEnrolmentConfirmTerm}
                        onClose={() => setShowOpenEnrolmentConfirmModal(false)}
                        title={openEnrolmentConfirmTerm ? `Open enrolment for ${openEnrolmentConfirmTerm.name}?` : ""}
                        icon={mdiLockOpen}
                        maxWidth={480}
                        footer={
                            <>
                                <button className="base-modal-cancel-btn" onClick={() => setShowOpenEnrolmentConfirmModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    className="base-modal-save-btn"
                                    onClick={() => {
                                        setShowOpenEnrolmentConfirmModal(false);
                                        handleOpenEnrolment(openEnrolmentConfirmTerm);
                                        setOpenEnrolmentConfirmTerm(null);
                                    }}>
                                    Yes, open enrolment
                                </button>
                            </>
                        }
                    >
                        <p style={{ marginTop: 0, marginBottom: 12, color: "var(--dl-color-gray-700)", fontSize: "0.95rem" }}>
                            This action cannot be undone. Once enrolment is open, students can be enrolled and payment reminders can be sent.
                        </p>
                        {openEnrolmentConfirmTerm && (classesMap[openEnrolmentConfirmTerm.id] || []).filter(c => c.isActive !== false).length === 0 && (
                            <div className="class-terms-modal-warning-banner">
                                <Icon path={mdiAlertOutline} size={0.9} style={{ flexShrink: 0 }} />
                                <p>
                                    <strong>This term has no classes yet.</strong> Make sure you add classes before sending enrolment reminders so students know what they are signing up for.
                                </p>
                            </div>
                        )}
                        <p className="class-term-form-hint" style={{ marginTop: 8 }}>
                            Before proceeding, confirm that all students have been added or rolled over from the previous term. Once open, you will have the option to send payment reminder emails to confirmed students.
                        </p>
                    </BaseModal>

                    {/* Close enrolment confirmation gate */}
                    <BaseModal
                        isOpen={showCloseEnrolmentConfirmModal && !!closeEnrolmentConfirmTerm}
                        onClose={() => setShowCloseEnrolmentConfirmModal(false)}
                        title={closeEnrolmentConfirmTerm ? `Close enrolment for ${closeEnrolmentConfirmTerm.name}?` : ""}
                        icon={mdiLock}
                        maxWidth={480}
                        footer={
                            <>
                                <button className="base-modal-cancel-btn" onClick={() => setShowCloseEnrolmentConfirmModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    className="base-modal-save-btn"
                                    onClick={() => {
                                        setShowCloseEnrolmentConfirmModal(false);
                                        handleCloseEnrolment(closeEnrolmentConfirmTerm);
                                        setCloseEnrolmentConfirmTerm(null);
                                    }}>
                                    Yes, close enrolment
                                </button>
                            </>
                        }
                    >
                        <p style={{ marginTop: 0, marginBottom: 12, color: "var(--dl-color-gray-700)", fontSize: "0.95rem" }}>
                            Are you sure? Closing enrolment hides this term from your public class pages, so any shared link to it will fall back to showing your current term instead.
                        </p>
                        <p className="class-term-form-hint" style={{ marginTop: 0 }}>
                            Existing enrolments, payments and student data are not affected. You can re-open enrolment again at any time.
                        </p>
                    </BaseModal>

                    {/* Payment reminder prompt (post-open or standalone) */}
                    <BaseModal
                        isOpen={showEnrolmentReminderModal}
                        onClose={() => setShowEnrolmentReminderModal(false)}
                        title={enrolmentReminderContext === 'open' ? "Notify students enrolment is open" : "Send payment reminders"}
                        icon={mdiEmailOutline}
                        maxWidth={480}
                        footer={
                            <>
                                <button className="base-modal-cancel-btn" onClick={() => setShowEnrolmentReminderModal(false)}>
                                    No / Later
                                </button>
                                <button
                                    className="base-modal-save-btn"
                                    onClick={openPaymentReminderEmailReview}
                                    disabled={isSendingReminders}>
                                    {isSendingReminders ? "Sending\u2026" : enrolmentReminderContext === 'open' ? "Yes, send invitations" : "Yes, send reminders"}
                                </button>
                            </>
                        }
                    >
                        {enrolmentReminderContext === 'open' ? (
                            <>
                                <p style={{ marginTop: 0, marginBottom: 12, color: "var(--dl-color-gray-700)", fontSize: "0.95rem" }}>
                                    Send enrolment invitation emails to confirmed students? Each student will be told they have been placed in their classes from last term and given a secure link to review and pay.
                                </p>
                                <p className="class-term-form-hint">
                                    Only confirmed students will receive the email. Students without an email address on file are skipped.
                                </p>
                            </>
                        ) : (
                            <>
                                <p style={{ marginTop: 0, marginBottom: 12, color: "var(--dl-color-gray-700)", fontSize: "0.95rem" }}>
                                    Send payment reminder emails to confirmed students who have not paid yet? Each student will receive a secure link to review and pay.
                                </p>
                                <p className="class-term-form-hint">
                                    Only confirmed students with an outstanding balance will receive the email. Already-paid students are skipped.
                                </p>
                            </>
                        )}
                    </BaseModal>


                    {/* Short Course Group modal */}
                    {showGroupModal && (
                        <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
                            <div className="class-terms-modal-content" onClick={e => e.stopPropagation()}>
                                <div className="class-terms-modal-header">
                                    <h2>
                                        <Icon
                                            path={mdiLayersOutline}
                                            size={0.9}
                                            color="#451f78"
                                            style={{ marginRight: 8, verticalAlign: "middle" }}
                                        />
                                        {editingGroup ? "Edit Group" : "New Group"}
                                    </h2>
                                    <button
                                        className="class-terms-modal-close"
                                        onClick={() => setShowGroupModal(false)}>
                                        &times;
                                    </button>
                                </div>
                                <div className="class-terms-modal-body">
                                    <label className="class-term-form-label">Name *</label>
                                    <input
                                        className="class-term-form-input"
                                        placeholder="e.g. Bossa Weekender 2026"
                                        value={grpName}
                                        onChange={e => setGrpName(e.target.value)}
                                    />
                                    <label className="class-term-form-label">Type label</label>
                                    <p className="class-term-form-hint">What kind of event is this? e.g. Weekender, Congress, Festival</p>
                                    <input
                                        className="class-term-form-input"
                                        placeholder="e.g. Weekender"
                                        value={grpLabel}
                                        onChange={e => setGrpLabel(e.target.value)}
                                    />
                                    <label className="class-term-form-label">Description</label>
                                    <input
                                        className="class-term-form-input"
                                        placeholder="Optional description"
                                        value={grpDescription}
                                        onChange={e => setGrpDescription(e.target.value)}
                                    />
                                    <div className="class-term-form-row">
                                        <div>
                                            <label className="class-term-form-label">Start date</label>
                                            <DateInputWithPicker
                                                value={grpStartDate}
                                                onChange={setGrpStartDate}
                                                placeholder="YYYY-MM-DD"
                                            />
                                        </div>
                                        <div>
                                            <label className="class-term-form-label">End date</label>
                                            <DateInputWithPicker
                                                value={grpEndDate}
                                                onChange={setGrpEndDate}
                                                placeholder="YYYY-MM-DD"
                                            />
                                        </div>
                                    </div>
                                    <label className="class-term-form-label">Location</label>
                                    <input
                                        className="class-term-form-input"
                                        placeholder="e.g. Studio Nova, Melbourne"
                                        value={grpLocation}
                                        onChange={e => setGrpLocation(e.target.value)}
                                    />
                                </div>
                                <div className="class-terms-modal-footer">
                                    <button className="class-term-form-cancel" onClick={() => setShowGroupModal(false)}>
                                        Cancel
                                    </button>
                                    <button className="class-term-form-save" onClick={handleGroupSave} disabled={isGrpSaving}>
                                        {isGrpSaving ? "Saving..." : editingGroup ? "Update Group" : "Create Group"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
        <ClassTermsHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

        <EmailReviewModal
            isOpen={showEmailReviewModal}
            emailType={emailReviewType}
            templateData={emailReviewData}
            audienceDescription={emailReviewAudienceDescription}
            recipientCount={emailReviewRecipientCount}
            onConfirm={(subject, bodyHtml) => emailReviewOnConfirm && emailReviewOnConfirm(subject, bodyHtml)}
            onCancel={() => setShowEmailReviewModal(false)}
            isConfirming={isConfirmingSend}
        />

        {confirmModal && (
            <ConfirmationModal
                isOpen
                title={confirmModal.title}
                message={confirmModal.message}
                variant={confirmModal.variant}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(null)}
            />
        )}
        </>
    );
};

export default ClassTermsPage;
